import React, { useEffect, useState } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { PrevBtn, NextBtn, PaginationWrapper } from "../../Styles/PageStyles";
import Images from "../../../Images";
import tw from "tailwind-styled-components";
import { PaginationLimit } from "../../../Config";
import { capitalizeFirstLetter, getCategoryIcon, getFoodCategoryLabel, getFoodUnitLabel, getStockStatus, handleCopy, truncateText } from "../../../helpers";
import moment from "moment";
import { IoCopySharp } from "react-icons/io5";
import { getDateTimeDisplay } from "../../../utilities/helpers";


const Table = ({ apiData }) => {
    const [data, setData] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
    const [currentPage, setCurrentPage] = useState(0);

    const toggleRow = (index) => {
        setSelectedRow(selectedRow === index ? null : index);
    };

    useEffect(() => {
        if (apiData) {
            const tempData = apiData.map((item) => ({
                itemName: getItemName(item),
                currentCount: getStockCount(item),
                status: getStatus(item),

                updatedAt: getDateTimeDisplay (item.updatedAt),
            }));
            setData(tempData);
        }
    }, [apiData]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const getItemName = (item) => {
        const label = getFoodCategoryLabel(item.categoryId);
        return (
            <UserImageWrapper>
                <IconWrapper>
                    {getCategoryIcon(item.categoryId)}
                </IconWrapper>
                <div className="flex flex-col min-w-0">
                    <div className="flex items-center gap-2 group ">
                        <span className="font-medium text-gray-700 truncate">
                            {capitalizeFirstLetter(truncateText(item.itemName, 28))}
                        </span>
                    </div>
                    <span className="text-gray-400 text-xs">{label}</span>
                </div>
            </UserImageWrapper>
        );
    };

    const getStockCount = (item) => {
        return (
            <div className="flex items-baseline gap-1.5">
                <span className="text-sm font-medium text-gray-600 tabular-nums">
                    {item.currentCount}
                </span>

                <span className="text-xs text-gray-400">
                    {getFoodUnitLabel(item.unitType)}
                </span>
            </div>
        );
    };

    const getStatus = (item) => {
        const status = getStockStatus(item.currentCount);

        return (
            <span
                className={`
                inline-flex items-center
                px-2.5 py-1
                rounded-md
                text-sm font-medium
                ${status.bg}
                ${status.color}
            `}
            >
                {status.label}
            </span>
        );
    };

    const columns = React.useMemo(() => {
        if (isMobileView) {
            return [
                {
                    Header: "Item",
                    accessor: "itemName",
                },
                {
                    Header: "Stock",
                    accessor: "currentCount",
                },

            ];
        }
        return [
            {
                Header: "Item",
                accessor: "itemName",
            },

            {
                Header: "Available",
                accessor: "currentCount",
            },
            {
                Header: "Status",
                accessor: "status",
            },
            {
                Header: "Last Updated",
                accessor: "updatedAt",
            },
        ];
    }, [isMobileView]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        pageOptions,
        page,
        state: { pageIndex },
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
    } = useTable(
        {
            data,
            columns,
            initialState: { pageIndex: currentPage, pageSize: PaginationLimit },
        },
        useSortBy,
        usePagination
    );

    useEffect(() => {
        setCurrentPage(pageIndex);
    }, [pageIndex]);

    return (
        <>
            <CustomTable {...getTableProps()}>
                <Thead>
                    {headerGroups.map((headerGroup) => (
                        <Tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <Th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                    {column.render("Header")}
                                    <span>
                                        {column.isSorted ? (
                                            <img
                                                src={Images.Arrow}
                                                alt="down arrow"
                                                className={`${column.isSortedDesc ? "-rotate-90" : "rotate-90"
                                                    } w-1.5 inline-block ml-1.5`}
                                            />
                                        ) : (
                                            ""
                                        )}
                                    </span>
                                </Th>
                            ))}
                        </Tr>
                    ))}
                </Thead>
                <Tbody {...getTableBodyProps()} hasdata={(apiData?.length > 0).toString()}>
                    {page.map((row, rowIndex) => {
                        prepareRow(row);
                        return (
                            <React.Fragment key={rowIndex}>
                                <Tr {...row.getRowProps({ style: { height: "80px" } })} hasdata={(apiData?.length > 0).toString()}>
                                    {row.cells.map((cell, cellIndex) => {
                                        return (
                                            <Td
                                                {...cell.getCellProps()}
                                                onClick={() => {
                                                    if (
                                                        cellIndex === 0 ||
                                                        (cellIndex === 1 && window.innerWidth < 768)
                                                    ) {
                                                        toggleRow(rowIndex);
                                                    }
                                                }}
                                            >
                                                {cell.render("Cell")}
                                            </Td>
                                        );
                                    })}
                                </Tr>
                                {selectedRow === rowIndex && window.innerWidth < 768 && (
                                    <Tr>
                                        <Td colSpan={columns.length}>
                                            <ul className="flex flex-col gap-2">


                                                <li>
                                                    <strong>Last Updated:</strong>{" "}
                                                    {data[rowIndex]?.updatedAt}
                                                </li>
                                            </ul>
                                        </Td>
                                    </Tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </Tbody>
            </CustomTable>
            {apiData?.length > 0 && (
                <PaginationWrapper>
                    <div className="px-2">
                        Page{" "}
                        <em>
                            {pageIndex + 1} of {pageOptions.length}
                        </em>
                    </div>
                    <div className="flex gap-1">
                        <PrevBtn onClick={() => previousPage()} disabled={!canPreviousPage}>
                            <img src={Images.Arrow} alt="arrow" className="w-2 opacity-75" />
                        </PrevBtn>
                        <NextBtn onClick={() => nextPage()} disabled={!canNextPage}>
                            <img
                                src={Images.Arrow}
                                alt="arrow"
                                className="w-2 rotate-180 opacity-75"
                            />
                        </NextBtn>
                    </div>
                </PaginationWrapper>
            )}
        </>
    );
};

const UserImageWrapper = tw.div`flex items-center gap-2`;
const IconWrapper = tw.div`
  w-9 h-9
  flex items-center justify-center
  rounded-full
  bg-primary-color/10
  text-primary-color
  flex-shrink-0
`;
const CategoryWrapper = tw.div`flex items-center gap-2`;
const CustomTable = tw.table`w-full text-zinc-600`;
const Thead = tw.thead`border-b uppercase text-gray-600 bg-blue-50`;
const Tbody = tw.tbody`${(props) => (props.hasdata === "true" ? "border-b" : "")}`;
const Tr = tw.tr`${(props) =>
    props.hasdata === "true"
        ? "border-b"
        : ""} rounded-md overflow-hidden hover:bg-slate-50`;
const Th = tw.th`text-left text-sm md:p-3 p-0.5 font-normal text-gray-600`;
const Td = tw.td`p-1 text-sm md:p-2 text-left`;

export default Table;